import { apiRequestJson, buildApiUrl } from '../api';
import {
  adoptRuntimeAuthConfig,
  normaliseAuthProviderName,
  type AuthProviderName,
  type ScalekitBrowserConfig,
} from '../auth/runtimeAuthConfig';

export type PublicConfig = {
  stripePublishableKey: string;
  /**
   * Gates the self-service onboarding wizard at /signup. The backend does
   * not emit this field yet; absent means off, so the flow ships dark.
   */
  selfServiceSignupEnabled: boolean;
  /**
   * Base URL of the Lutiq MCP server (e.g. `https://api.lutiq.com/mcp`), used
   * by the "Author via MCP" connect snippet so it's correct per environment
   * rather than hardcoded. Empty when the backend doesn't emit it — callers
   * render a "see docs" fallback instead of a broken command.
   */
  mcpServerUrl: string;
  /**
   * Which identity provider ADMINISTERS users, i.e. whether this deployment has
   * passwords at all (US-009).
   *
   * Read from the live `IdentityAdminClient`. Password-shaped UI — change
   * password, forgot/reset password, the password field on the accept-invite
   * form — branches on THIS.
   *
   * NOT the same question as {@link loginProvider}. Before US-011 the two moved
   * together and one field answered both; they are separate switches now, so a
   * deployment can administer users in Scalekit without yet sending browsers
   * there, or accept Scalekit tokens without either.
   *
   * Absent (older backend) or unrecognised normalises to `'keycloak'`: the
   * failure that shows a password form on a passwordless deployment is a 409
   * the user can read, while the failure that hides it on a password deployment
   * locks people out.
   */
  authProvider: AuthProviderName;
  /**
   * Which auth ADAPTER the SPA must run (US-011).
   *
   * The runtime replacement for the build-time `VITE_AUTH_PROVIDER` flag, and
   * the thing that makes the login cutover independently reversible. Absent
   * (older backend) or unrecognised normalises to `'keycloak'`, for the same
   * reason as above and with more at stake: this one decides where every
   * sign-in redirect goes.
   */
  loginProvider: AuthProviderName;
  /**
   * The non-secret Scalekit browser client, or null when this deployment must
   * not build one. Null whenever `loginProvider` is keycloak.
   */
  scalekitLogin: ScalekitBrowserConfig | null;
};

type ScalekitLoginApi = {
  issuer?: string;
  client_id?: string;
  organization_id?: string;
  authorize_endpoint?: string;
  token_endpoint?: string;
  jwks_endpoint?: string;
  end_session_endpoint?: string;
  scope?: string;
  google_hint?: string;
  google_param?: string;
};

type PublicConfigApi = {
  stripe_publishable_key: string;
  self_service_signup_enabled?: boolean;
  mcp_server_url?: string;
  /** `keycloak` | `scalekit`; snake_case on the wire. Optional pre-US-009. */
  auth_provider?: string;
  /** `keycloak` | `scalekit`. Optional pre-US-011. */
  login_provider?: string;
  /** Omitted entirely unless this deployment advertises Scalekit login. */
  scalekit_login?: ScalekitLoginApi | null;
};

/** Blank-is-absent, so an empty string never becomes a configured endpoint. */
const optional = (raw: string | undefined): string | undefined => {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * The served client, or null.
 *
 * <p>Null unless BOTH `issuer` and `client_id` are present: those are the two
 * fields no authorization request can be assembled without, and a half-config
 * that produced a client would send users to a request the provider rejects.
 * The backend already refuses to serve one in that state; this is the second
 * check, because the cost of getting it wrong is a lockout.
 */
const toScalekitLogin = (
  payload: ScalekitLoginApi | null | undefined,
): ScalekitBrowserConfig | null => {
  const issuer = optional(payload?.issuer);
  const clientId = optional(payload?.client_id);
  const organizationId = optional(payload?.organization_id);
  if (
    !issuer ||
    !clientId ||
    !organizationId?.startsWith('org_') ||
    organizationId.length === 4
  ) return null;
  return {
    issuer,
    clientId,
    organizationId,
    authorizeEndpoint: optional(payload?.authorize_endpoint),
    tokenEndpoint: optional(payload?.token_endpoint),
    jwksEndpoint: optional(payload?.jwks_endpoint),
    endSessionEndpoint: optional(payload?.end_session_endpoint),
    scope: optional(payload?.scope),
    googleHint: optional(payload?.google_hint),
    googleParam: optional(payload?.google_param),
  };
};

export const fetchPublicConfig = async (): Promise<PublicConfig> => {
  const payload = await apiRequestJson<PublicConfigApi>(
    buildApiUrl('/api/config/public'),
  );
  const scalekitLogin = toScalekitLogin(payload.scalekit_login);
  const loginProvider = normaliseAuthProviderName(payload.login_provider);
  return {
    stripePublishableKey: payload.stripe_publishable_key,
    selfServiceSignupEnabled: payload.self_service_signup_enabled ?? false,
    mcpServerUrl: payload.mcp_server_url ?? '',
    authProvider: normaliseAuthProviderName(payload.auth_provider),
    // A `scalekit` answer with no usable client is not an answer. Degrading
    // here as well as in the store keeps the two readers of this value —
    // `useLoginProviderName` for invite routing, `runtimeAuthConfig` for the
    // adapter — from disagreeing about a half-configured deployment.
    loginProvider: loginProvider === 'scalekit' && scalekitLogin ? 'scalekit' : 'keycloak',
    scalekitLogin,
  };
};

/**
 * Fetch the public config and adopt its auth half, BEFORE the session bootstrap
 * dispatches to an adapter (US-011).
 *
 * <h2>Who awaits this, and who must not</h2>
 *
 * `startSession()` processes an OAuth callback, and a callback has to be
 * processed by the adapter that started it — so the ONE dispatch that genuinely
 * needs the runtime answer first is completing a callback the Scalekit adapter
 * began. `AuthContext.boot()` therefore fires this WITHOUT awaiting it and
 * awaits only when an authorization response coincides with a pending Scalekit
 * PKCE transaction, a condition unreachable on a Keycloak deployment.
 *
 * The second waiter is `AuthContext`'s `commitLoginRedirect`, for the same
 * class of reason: a login dispatch ends in a full-page navigation, so an early
 * answer cannot be corrected afterwards. It waits on the promise `boot()`
 * already started rather than issuing its own, and its wait is bounded — so a
 * hanging endpoint delays a sign-in redirect by that bound instead of parking
 * it forever.
 *
 * Awaiting it unconditionally — as this did until the boot path was corrected —
 * put a network round trip in front of every page load in every environment,
 * and, because `requestJson` sets no timeout and no `AbortSignal`, a config
 * endpoint that HUNG rather than failed hung the boot spinner indefinitely. An
 * unreachable endpoint never showed this: it rejects promptly and is caught
 * below. Do not reintroduce the unconditional await.
 *
 * The cold-load path is safe unawaited because the adapters are asymmetric:
 * `scalekit.runBootstrap()` probes with a `prompt=none` iframe and returns
 * false rather than navigating, so a dispatch made before the config lands
 * cannot send the browser anywhere it should not go.
 *
 * <h2>Why every failure is swallowed</h2>
 *
 * The endpoint being unreachable must not stop the SPA booting. With nothing
 * adopted, `resolveAuthProviderName()` falls through to the build-time flag and
 * then to keycloak — the same answer this SPA has always given — so a dead
 * config endpoint degrades to "the cutover has not reached this tab", never to
 * a blank page.
 */
export const loadRuntimeAuthConfig = async (): Promise<void> => {
  try {
    const config = await fetchPublicConfig();
    adoptRuntimeAuthConfig({
      loginProvider: config.loginProvider,
      scalekit: config.scalekitLogin,
    });
  } catch {
    // Keep the build-time fallback. See above.
  }
};
